import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const CodeBlock = makeShortcode("CodeBlock");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "network-io"
    }}>{`Network I/O`}</h1>
    <p>{`You may be looking for ways to make network calls for various reasons such as fetching an image, accessing a web API, browsing remote resources, synchronizing content, etc. UXP supports a range of network APIs that can fit your use case. `}</p>
    <InlineAlert variant="info" slots="header, text1, text2" mdxType="InlineAlert" />
    <p>{`Plugins and Scripts`}</p>
    <p><strong parentName="p">{`In plugins`}</strong>{`, you should seek permission for \``}<inlineCode parentName="p">{`access in the manifest and specify the`}</inlineCode>{`domains\` of the endpoints. `}<br></br>{`
IMPORTANT: Please read about the `}<a parentName="p" {...{
        "href": "../../../plugins/concepts/manifest/#networkpermission"
      }}>{`manifest permissions`}</a>{` module before you proceed.`}</p>
    <p><strong parentName="p">{`In scripts`}</strong>{`, the permission for `}<inlineCode parentName="p">{`network`}</inlineCode>{` is fixed. You can ignore the manifest details in the following examples. Learn about these values in the `}<a parentName="p" {...{
        "href": "../../fundamentals/manifest/"
      }}>{`manifest fundamentals section`}</a>{`. `}</p>
    <p>{`The fastest way to try out a network call is by rendering an image from the web`}</p>
    <CodeBlock slots="heading, code" repeat="2" languages="HTML, JSON" mdxType="CodeBlock" />
    <h4 {...{
      "id": "html"
    }}>{`HTML`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<img src='https://source.unsplash.com/random' />
`}</code></pre>
    <h4 {...{
      "id": "manifest"
    }}>{`manifest`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "network": {
            "domains": [
                "https://source.unsplash.com"
            ]
        }
    }
}
`}</code></pre>
    <p>{`As mentioned earlier, UXP has `}<inlineCode parentName="p">{`fetch`}</inlineCode>{`, `}<inlineCode parentName="p">{`XHR (XML HTTP Request)`}</inlineCode>{`, and `}<inlineCode parentName="p">{`WebSocket`}</inlineCode>{` available in the global scope. If you aren't aware of the difference and want to understand which one is the right option for you, we recommend you learn about them first - `}<a parentName="p" {...{
        "href": "https://javascript.info/fetch"
      }}>{`fetch`}</a>{` | `}<a parentName="p" {...{
        "href": "https://javascript.info/websocket"
      }}>{`WebSocket`}</a>{` | `}<a parentName="p" {...{
        "href": "https://javascript.info/xmlhttprequest"
      }}>{`XHR`}</a></p>
    <h2 {...{
      "id": "system-requirements"
    }}>{`System requirements`}</h2>
    <p>{`Please make sure your local environment uses the following application versions before proceeding.`}</p>
    <ul>
      <li parentName="ul">{`InDesign v18.5 or higher`}</li>
      <li parentName="ul">{`UDT v1.9.0 or higher`}</li>
      <li parentName="ul">{`Manifest version v5 or higher`}</li>
    </ul>
    <h2 {...{
      "id": "examples"
    }}>{`Examples`}</h2>
    <p>{`Now, let's look at some examples.`}</p>
    <h3 {...{
      "id": "fetch"
    }}>{`fetch`}</h3>
    <CodeBlock slots="heading, code" repeat="2" languages="JavaScript, JSON" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript"
    }}>{`JavaScript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`async function foo() {
    // Get weather forecast for San Jose
    try {
        const response = await fetch(\`https://api.weather.gov/gridpoints/MTR/99,82/forecast\`);
        if (!response.ok) {
            throw new Error(\`HTTP error fetching weather station; status: \${response.status}\`);
        }
        const responseJson = await response.json();
        console.log(\`Forecast \${responseJson.properties.periods[0].detailedForecast}\`);

    } catch (e) {
        console.error(e);
    }
}
`}</code></pre>
    <h4 {...{
      "id": "manifest-1"
    }}>{`manifest`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "network": {
            "domains": [
                "https://api.weather.gov"
            ]
        }
    }
}
`}</code></pre>
    <h3 {...{
      "id": "xhr"
    }}>{`XHR`}</h3>
    <CodeBlock slots="heading, code" repeat="2" languages="JavaScript, JSON" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript-1"
    }}>{`JavaScript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`function foo() {
    // Get weather forecast for San Jose via XHR
    const xhr = new XMLHttpRequest();
    xhr.open('GET', 'https://api.weather.gov/gridpoints/MTR/99,82/forecast');
    xhr.responseType = 'json';
    xhr.send();

    xhr.onload = function() {
        const responseObj = xhr.response;
        console.log(\`Forecast \${responseObj.properties.periods[0].detailedForecast}\`);
    };
}
`}</code></pre>
    <h4 {...{
      "id": "manifest-2"
    }}>{`manifest`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "network": {
            "domains": [
                "https://api.weather.gov"
            ]
        }
    }
}
`}</code></pre>
    <h3 {...{
      "id": "websockets"
    }}>{`WebSockets`}</h3>
    <CodeBlock slots="heading, code" repeat="2" languages="JavaScript, JSON" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript-2"
    }}>{`JavaScript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`let socket;
async function foo() {
    // Establish web socket connection
    if (!!socket) {
        console.log("Already connected; disconnecting first.");
        await socket.close();
        return;
      }

    socket = new WebSocket("wss://javascript.info/article/websocket/demo/hello");

    socket.onopen = function(e) {
      console.log("Connection established");
      // sending data to server
      socket.send("My name is John");
    };
    
    socket.onmessage = function(event) {
      alert(\`Data received from server: \${event.data}\`);
    };
    
    socket.onclose = function(event) {
      console.log("Connection closed");
      socket = null;
    };
    
    socket.onerror = function(error) {
      console.error(\`Connection error \${error}\`);
    };
}
`}</code></pre>
    <h4 {...{
      "id": "manifest-3"
    }}>{`manifest`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "network": {
            "domains": [
                "wss://javascript.info"
            ]
        }
    }
}
`}</code></pre>
    <h2 {...{
      "id": "additional-notes"
    }}>{`Additional notes`}</h2>
    <ul>
      <li parentName="ul">{`These APIs are available in the global scope. Observe that you didn't have to mount them using `}<inlineCode parentName="li">{`require`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`domains`}</inlineCode>{` in the plugin's manifest also let you configure variations of the endpoints. For example, your plugin may like to access 'api.dev.example.com', 'api.stage.example.com' or 'api.prod.example.com' depending upon the environment. You can configure the domains with `}<inlineCode parentName="li">{`["https://api.*.example.com"]`}</inlineCode>{` to manage all three.`}</li>
      <li parentName="ul">{`While using `}<inlineCode parentName="li">{`http`}</inlineCode>{` domains, keep in mind that Photoshop only allows http protocol on Win32 but not on macOS. InDesign has no such restrictions.`}</li>
    </ul>
    <h2 {...{
      "id": "reference-material"
    }}>{`Reference material`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Global%20Members/Data%20Transfers/fetch/"
        }}>{`fetch API`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Global%20Members/Data%20Transfers/XMLHttpRequest/"
        }}>{`XHR API`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Global%20Members/Data%20Transfers/WebSocket/"
        }}>{`WebSocket API`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      